import type { OrderDetail, ProductionOrderSheetFields } from '@repo/api-contracts';
import { type ReactElement, useEffect } from 'react';
import { useProductionOrderSheetQuery } from '@/entities/order';
import {
  ProductionOrderSheetForm,
  useSaveProductionOrderSheet,
} from '@/features/order/production-order-sheet';
import { formatDateTime } from '@/shared/lib';
import { ContentCard, EmptyState } from '@/shared/ui';

interface ProductionOrderSheetSectionProps {
  readonly orderId: string;
  readonly order: OrderDetail;
}

/** 저장본에서 입력 칸만 뽑는다 — id·updatedAt 같은 메타가 저장 요청(strict 스키마)에 섞이지 않게. */
const toSheetFields = (sheet: ProductionOrderSheetFields): ProductionOrderSheetFields => ({
  supplierName: sheet.supplierName,
  supplierContact: sheet.supplierContact,
  supplierPhone: sheet.supplierPhone,
  supplierEmail: sheet.supplierEmail,
  dueDate: sheet.dueDate,
  lines: sheet.lines,
  note: sheet.note,
});

/**
 * 주문 상세의 발주서 영역 — 결제 완료(PAID) 주문만 작성한다. 취소·환불·결제 대기 주문은 한 줄 안내만 보이고 조회도 하지 않는다.
 * 주소 해시가 `#production-order` 면 처음 그릴 때 이 카드로 스크롤한다.
 */
export const ProductionOrderSheetSection = ({
  orderId,
  order,
}: ProductionOrderSheetSectionProps): ReactElement => {
  const refundPending = order.bankTransfer?.status === 'REFUND_PENDING';
  const writable = order.status === 'PAID' && !refundPending;
  const query = useProductionOrderSheetQuery(orderId, { enabled: writable });
  const save = useSaveProductionOrderSheet();

  useEffect(() => {
    if (window.location.hash !== '#production-order') return;
    const element = document.getElementById('production-order');
    if (element && typeof element.scrollIntoView === 'function') element.scrollIntoView();
  }, []);

  const view = query.data;
  const sheet = view?.sheet ?? null;
  const description = sheet
    ? `마지막 저장 ${formatDateTime(sheet.updatedAt)} · ${sheet.updatedByName ?? '탈퇴 회원'}`
    : undefined;

  let body: ReactElement;
  if (order.status === 'CANCELED' || order.status === 'REFUNDED') {
    body = (
      <p className="text-sm text-muted-foreground">
        취소·환불된 주문은 발주서를 작성할 수 없습니다.
      </p>
    );
  } else if (refundPending) {
    body = (
      <p className="text-sm text-muted-foreground">환불 대기 중에는 발주서를 작성할 수 없습니다.</p>
    );
  } else if (!writable) {
    body = <p className="text-sm text-muted-foreground">결제 완료 후 작성할 수 있습니다.</p>;
  } else if (query.isPending) {
    body = <EmptyState>불러오는 중…</EmptyState>;
  } else if (!view) {
    body = <EmptyState isError>발주서를 불러오지 못했습니다.</EmptyState>;
  } else {
    body = (
      <ProductionOrderSheetForm
        initial={view.sheet ? toSheetFields(view.sheet) : view.defaults}
        onSave={(fields) => save({ orderId, ...fields })}
        printHref={`/orders/${orderId}/production-order/print`}
      />
    );
  }

  return (
    <ContentCard id="production-order" title="발주서" description={description}>
      {body}
    </ContentCard>
  );
};
